/**
 * Catálogo de permisos granulares · espejo de lib/agent_permissions.rb (Ruby).
 * Mantener sincronizado: si cambias claves aquí, también en el backend.
 *
 * Estructura del feature_permissions JSON guardado por agente:
 *   { "_mode": "legacy" | "custom",
 *     "modules": { "chat.view": true, "leads_deals.create": true, ... },
 *     "move_lead_between_stages": true, ...
 *   }
 *
 * - "_mode" ausente o "legacy" → full access (default histórico)
 * - "_mode" === "custom" → solo lo marcado true en modules{} es permitido
 */

export const CRUD = ['view', 'edit', 'create', 'delete', 'export'];

// Estructura jerárquica para UI (categorías + módulos con sus acciones aplicables).
// El orden aquí determina el orden visual en el modal.
export const CATEGORIES = [
  {
    key: 'chats',
    label: 'Chats y Conversaciones',
    icon: '💬',
    modules: [
      { key: 'chat',                    label: 'Chat con cliente',         icon: '💬', actions: ['view', 'edit', 'create', 'delete', 'export'], desc: 'Conversaciones, mensajes, attachments' },
      { key: 'conversation_assignment', label: 'Asignación conversaciones', icon: '📌', actions: ['view', 'edit'], desc: 'Asignar conversaciones a otros agentes' },
      { key: 'internal_notes',          label: 'Notas internas',           icon: '📝', actions: ['view', 'edit', 'create', 'delete'], desc: 'Notas privadas dentro de las conversaciones' },
      { key: 'macros',                  label: 'Macros',                   icon: '📋', actions: ['view', 'edit', 'create', 'delete'], desc: 'Acciones predefinidas reutilizables' },
      { key: 'canned_responses',        label: 'Canned Responses',         icon: '💬', actions: ['view', 'edit', 'create', 'delete'], desc: 'Respuestas rápidas predefinidas' },
      { key: 'message_templates',       label: 'Plantillas de Mensajes',   icon: '✦', actions: ['view', 'edit', 'create', 'delete'], desc: 'Plantillas predefinidas para enviar al cliente' },
    ],
    specialActions: [
      { key: 'branch_manager', label: '👔 Gerente de sucursal', desc: 'Ve TODAS las conversaciones de SU sucursal (asignadas + sin asignar), no solo las propias · nada de otras sucursales' },
      { key: 'seleccion_retoque', label: '🎯 Selección', desc: 'Además de sus asignados, ve en su chat TODOS los clientes de los embudos DTO. DE SELECCIÓN y DTO. DE RETOQUE de SU sucursal' },
      { key: 'cuentas_cobrar_scope', label: '🧾 Cuentas por Cobrar', desc: 'Ve en su chat (Todos) a TODOS los clientes con baucher detectado o factura cargada — aunque no sean sus asignados — y puede escribirles. Ve el icono 🧾 Por Facturar. En Leads solo entra a Ventas y Clientes' },
      { key: 'view_all_branch_chats', label: '👁️ Ver todos los chats', desc: 'Ve TODOS los chats de SU sucursal aunque no sean suyos (responsable, conversación y quién está en el perfil). Solo lectura: no escribe en los ajenos salvo que tenga "Responder a chats no asignados"' },
      { key: 'reply_unassigned_chats', label: '✍️ Responder a chats no asignados', desc: 'Levanta el candado de solo lectura: puede ESCRIBIR en chats de su sucursal que no son suyos sin ser el responsable. Requiere "Ver todos los chats" activo' },
      { key: 'mute_conversation', label: '🔇 Silenciar conversación', desc: 'Puede silenciar (y reactivar) conversaciones · una conversación silenciada NO se reabre cuando el cliente escribe. Sin este permiso el botón queda oculto' },
      { key: 'assign_tasks_to_others', label: '👥 Asignar tareas a otros', desc: 'Puede crear, editar y eliminar tareas asignándolas a otros agentes · el dropdown "Asignar a" se filtra por las sucursales accesibles del usuario (super_admin asigna a cualquiera)' },
    ],
  },
  {
    key: 'leads',
    label: 'Leads y Embudos · desglosado',
    icon: '🎯',
    modules: [
      { key: 'leads_deals', label: 'Leads / Deals',     icon: '🎴', actions: ['view', 'edit', 'create', 'delete', 'export'], desc: 'Las cards individuales del kanban' },
      { key: 'pipelines',   label: 'Embudos (pipelines)', icon: '🎯', actions: ['view', 'edit', 'create', 'delete'], desc: 'Crear/editar/eliminar embudos completos' },
      { key: 'stages',      label: 'Etapas (stages)',   icon: '📋', actions: ['view', 'edit', 'create', 'delete'], desc: 'Columnas dentro de cada embudo' },
    ],
    specialActions: [
      { key: 'move_lead_between_stages',    label: '↔ Mover lead entre etapas',     desc: 'Drag-and-drop o click · cambiar etapa dentro del MISMO embudo' },
      { key: 'move_lead_between_pipelines', label: '🔀 Mover lead a OTRO embudo',   desc: 'Reasignar a un embudo distinto · más restrictivo' },
      { key: 'view_reservations',           label: '🧾 Ver información de reserva',  desc: 'Muestra la tarjeta de Reserva del cliente (plan, precios, pagos) en el panel del lead' },
    ],
  },
  {
    key: 'analysis',
    label: 'Análisis e Informes',
    icon: '📊',
    modules: [
      { key: 'home',    label: 'Inicio · Dashboard', icon: '🏠', actions: ['view', 'export'], desc: 'Página de inicio con KPIs y resúmenes' },
      { key: 'reports', label: 'Informes / Reports', icon: '📊', actions: ['view', 'export'], desc: 'Reportes operativos y estadísticos' },
    ],
  },
  {
    key: 'tags_contacts',
    label: 'Etiquetas, Contactos y Custom Attributes',
    icon: '🏷',
    modules: [
      { key: 'labels',            label: 'Etiquetas (Labels)',     icon: '🏷', actions: ['view', 'edit', 'create', 'delete'], desc: 'Etiquetas que se aplican a conversaciones' },
      { key: 'contacts',          label: 'Contactos',              icon: '📇', actions: ['view', 'edit', 'create', 'delete', 'export'], desc: 'Base de clientes y sus datos' },
      { key: 'custom_attributes', label: 'Custom Attributes',      icon: '📁', actions: ['view', 'edit', 'create', 'delete'], desc: 'Campos personalizados de contactos/conversaciones' },
    ],
    specialActions: [
      { key: 'edit_client_phone', label: '📞 Editar número del cliente', desc: 'Puede CAMBIAR el teléfono del contacto. Vendedor/GM lo ven pero no lo editan' },
    ],
  },
  {
    key: 'channels',
    label: 'Canales e Inboxes',
    icon: '📨',
    modules: [
      { key: 'inboxes',      label: 'Inboxes',         icon: '📨', actions: ['view', 'edit', 'create', 'delete'], desc: 'Canales de comunicación (WhatsApp, Web, etc)' },
      { key: 'integrations', label: 'Integrations',    icon: '🔗', actions: ['view', 'edit', 'create', 'delete'], desc: 'Integraciones con servicios externos' },
    ],
  },
  {
    key: 'automation',
    label: 'Automatización (Bots, Salesbot, Automation)',
    icon: '🤖',
    modules: [
      { key: 'salesbot',   label: 'Salesbot por etapa', icon: '⚡', actions: ['view', 'edit', 'create', 'delete'], desc: 'Bots automáticos por etapa de embudo' },
      { key: 'bots',       label: 'Bots',               icon: '🤖', actions: ['view', 'edit', 'create', 'delete'], desc: 'Bots personalizados con triggers' },
      { key: 'automation', label: 'Automation rules',   icon: '⚡', actions: ['view', 'edit', 'create', 'delete'], desc: 'Reglas automáticas tipo if-then' },
    ],
  },
  {
    key: 'access',
    label: 'Usuarios y Acceso',
    icon: '👥',
    modules: [
      { key: 'agents',       label: 'Agentes',         icon: '👥', actions: ['view', 'edit', 'create', 'delete'], desc: 'Gestión de usuarios del sistema' },
      { key: 'teams',        label: 'Teams',           icon: '🛡', actions: ['view', 'edit', 'create', 'delete'], desc: 'Equipos de trabajo' },
      { key: 'branches',     label: 'Sucursales',      icon: '🏢', actions: ['view', 'edit', 'create', 'delete'], desc: 'Sucursales/branches del negocio' },
      { key: 'custom_roles', label: 'Custom Roles',    icon: '🔐', actions: ['view', 'edit', 'create', 'delete'], desc: 'Roles personalizados (Enterprise)' },
    ],
  },
  {
    key: 'advanced',
    label: 'Avanzado',
    icon: '⚙',
    modules: [
      { key: 'account_settings', label: 'Account Settings',    icon: '⚙', actions: ['view', 'edit'], desc: 'Configuración general de la cuenta' },
      { key: 'sla',              label: 'SLA',                 icon: '⏱', actions: ['view', 'edit', 'create', 'delete'], desc: 'Service Level Agreements' },
      { key: 'workflow',         label: 'Conversation Workflow', icon: '🔄', actions: ['view', 'edit', 'create', 'delete'], desc: 'Flujo de estados de conversaciones' },
      { key: 'audit_logs',       label: 'Audit Logs',          icon: '🔍', actions: ['view', 'export'], desc: 'Bitácora de auditoría' },
    ],
  },
];

export const ACTION_LABELS = {
  view:   { label: 'Ver',       icon: '👁' },
  edit:   { label: 'Editar',    icon: '✏' },
  create: { label: 'Crear',     icon: '➕' },
  delete: { label: 'Eliminar',  icon: '🗑' },
  export: { label: 'Exportar',  icon: '📤' },
};

// Lista plana de TODAS las claves válidas — espejo de AgentPermissions.all_keys (Ruby).
export function allKeys() {
  const keys = [];
  CATEGORIES.forEach(cat => {
    cat.modules.forEach(mod => {
      mod.actions.forEach(action => keys.push(`${mod.key}.${action}`));
    });
    (cat.specialActions || []).forEach(sa => keys.push(sa.key));
  });
  return keys;
}

export function totalKeysCount() {
  return allKeys().length;
}

// ── Preset Vendedor · set EXACTO (Victor 2026-05-16) ──────────────────
// Recepcionista que vende: atiende/escribe, trabaja y mueve leads, edita
// datos del cliente. NO toca estructura de embudos/etapas ni administración.
export const VENDEDOR_KEYS = [
  'chat.view', 'chat.edit', 'chat.create',
  'internal_notes.view', 'internal_notes.edit', 'internal_notes.create',
  'salesbot.view',
  'leads_deals.view', 'leads_deals.edit', 'leads_deals.create',
  'pipelines.view', 'stages.view',
  'home.view',
  'labels.view', 'labels.edit', 'labels.create',
  'contacts.view', 'contacts.edit', 'contacts.create',
  'custom_attributes.view', 'custom_attributes.edit',
];

// Base "operador completo" (lo que era el Vendedor amplio hasta 2026-06-07):
// atiende, mueve leads, edita cliente, etiquetas. Es la BASE de Selección y
// Gerente General — NO confundir con el nuevo Vendedor restringido de abajo.
function operatorFullBase() {
  const modules = {};
  VENDEDOR_KEYS.forEach(k => { modules[k] = true; });
  return {
    _mode: 'custom',
    modules,
    move_lead_between_stages: true,
    move_lead_between_pipelines: true,
    view_reservations: true,
  };
}

// ── P3 · Switches en lenguaje de negocio ──────────────────────────────
// Cada switch agrupa permisos técnicos. Vendedor = grupos 1-4 ON, 5-6 OFF.
export const BUSINESS_SWITCHES = [
  {
    key: 'attend', icon: '💬', label: 'Atender y escribir a clientes',
    desc: 'Chat, notas internas, enviar salesbot',
    keys: ['chat.view', 'chat.edit', 'chat.create',
      'internal_notes.view', 'internal_notes.edit', 'internal_notes.create',
      'salesbot.view'],
    special: [],
  },
  {
    key: 'leads', icon: '🎯', label: 'Trabajar leads',
    desc: 'Ver/crear leads y moverlos entre etapas y embudos',
    keys: ['leads_deals.view', 'leads_deals.edit', 'leads_deals.create',
      'pipelines.view', 'stages.view'],
    special: ['move_lead_between_stages', 'move_lead_between_pipelines'],
  },
  {
    key: 'client', icon: '👤', label: 'Editar datos del cliente',
    desc: 'Contactos, campos personalizados, etiquetas',
    keys: ['contacts.view', 'contacts.edit', 'contacts.create',
      'custom_attributes.view', 'custom_attributes.edit',
      'labels.view', 'labels.edit', 'labels.create'],
    special: [],
  },
  {
    key: 'home', icon: '🏠', label: 'Ver Inicio (KPIs)',
    desc: 'Dashboard de resúmenes',
    keys: ['home.view'], special: [],
  },
  {
    key: 'structure', icon: '🏗️', label: 'Modificar estructura de embudos/etapas',
    desc: 'Crear/renombrar/borrar embudos y columnas · peligroso',
    keys: ['pipelines.edit', 'pipelines.create', 'pipelines.delete',
      'stages.edit', 'stages.create', 'stages.delete'],
    special: [], danger: true,
  },
  {
    key: 'admin', icon: '⚙️', label: 'Administración del sistema',
    desc: 'Sucursales, bandejas, agentes, ajustes, automations…',
    keys: ['branches.view', 'branches.edit', 'branches.create', 'branches.delete',
      'inboxes.view', 'inboxes.edit', 'inboxes.create', 'inboxes.delete',
      'integrations.view', 'integrations.edit', 'integrations.create', 'integrations.delete',
      'agents.view', 'agents.edit', 'agents.create', 'agents.delete',
      'teams.view', 'teams.edit', 'teams.create', 'teams.delete',
      'custom_roles.view', 'custom_roles.edit', 'custom_roles.create', 'custom_roles.delete',
      'account_settings.view', 'account_settings.edit',
      'sla.view', 'sla.edit', 'sla.create', 'sla.delete',
      'workflow.view', 'workflow.edit', 'workflow.create', 'workflow.delete',
      'automation.view', 'automation.edit', 'automation.create', 'automation.delete',
      'bots.view', 'bots.edit', 'bots.create', 'bots.delete',
      'audit_logs.view', 'audit_logs.export'],
    special: [], danger: true,
  },
];

// ¿El grupo está completamente activo en estos permisos?
export function businessSwitchActive(perms, sw) {
  const mods = (perms && perms.modules) || {};
  const keysOk = sw.keys.every(k => mods[k] === true);
  const spOk = sw.special.every(s => perms && perms[s] === true);
  return keysOk && (sw.special.length === 0 || spOk);
}

// Activa/desactiva todo el grupo. Devuelve nuevos perms (inmutable).
export function applyBusinessSwitch(perms, sw, value) {
  const result = {
    ...perms,
    _mode: 'custom',
    modules: { ...((perms && perms.modules) || {}) },
  };
  sw.keys.forEach(k => { result.modules[k] = !!value; });
  sw.special.forEach(s => { result[s] = !!value; });
  return result;
}

// Plantillas predefinidas (espejo de AgentPermissions::PRESETS).
export const PRESETS = {
  full_access: {
    label: '👑 Super Admin',
    description: 'Acceso total a todo · todas las sucursales',
    apply: () => {
      const modules = {};
      const special = {};
      CATEGORIES.forEach(cat => {
        cat.modules.forEach(mod => {
          mod.actions.forEach(action => { modules[`${mod.key}.${action}`] = true; });
        });
        (cat.specialActions || []).forEach(sa => { special[sa.key] = true; });
      });
      return { _mode: 'custom', modules, ...special };
    },
  },
  read_only: {
    label: '👁 Solo lectura',
    description: 'Ver todo, no puede modificar nada',
    apply: () => {
      const modules = {};
      CATEGORIES.forEach(cat => {
        cat.modules.forEach(mod => {
          if (mod.actions.includes('view')) modules[`${mod.key}.view`] = true;
          if (mod.actions.includes('export')) modules[`${mod.key}.export`] = true;
        });
      });
      return { _mode: 'custom', modules, view_reservations: true };
    },
  },
  sales_agent: {
    label: '🛍 Vendedor',
    // Vendedor (redefinido Victor 2026-06-07): = Recepcionista + ver Leads/embudos.
    // Responde clientes, envía salesbot, plantillas/notas, cierra conversaciones,
    // entra a Leads, busca y abre clientes para escribirles. NO mueve leads, NO
    // elimina (conv/clientes), NO asigna tareas, NO reasigna clientes a otros.
    // Menú: SOLO Chats + Leads (sin Inicio ni Planes · hide_planes).
    description: 'SOLO Chats + Leads: responde clientes, salesbot, plantillas/notas, cierra conversaciones, entra a Leads y abre clientes para escribirles · NO mueve leads · NO elimina · NO asigna tareas ni reasigna clientes · sin Inicio ni Planes',
    apply: () => {
      const modules = {};
      [
        'chat.view', 'chat.edit', 'chat.create',
        'salesbot.view',
        'contacts.view',
        'canned_responses.view',
        'internal_notes.view', 'internal_notes.edit', 'internal_notes.create',
        'leads_deals.view',
        'pipelines.view', 'stages.view',
      ].forEach(k => { modules[k] = true; });
      // resolve_conversation = puede cerrar · hide_planes = oculta Planes del menú
      // (Leads usa el mismo permiso, por eso el flag). Sin move_*, sin delete,
      // sin assign_tasks_to_others, sin reassign_conversation.
      return { _mode: 'custom', modules, resolve_conversation: true, hide_planes: true };
    },
  },
  seleccion: {
    label: '🎯 Selección',
    description: 'Encargada suprema del depto de Selección/Retoque: ve TODA su sucursal + clientes de embudos Selección/Retoque + gestiona embudos y stages',
    // Mantener en sync con AgentPermissions.seleccion_keys (Victor 2026-05-20).
    // Base = operador completo (NO el Vendedor restringido).
    apply: () => {
      const base = operatorFullBase();
      return {
        ...base,
        modules: {
          ...base.modules,
          'pipelines.edit': true,
          'pipelines.create': true,
          'pipelines.delete': true,
          'stages.edit': true,
          'stages.create': true,
          'stages.delete': true,
        },
        branch_manager: true,
        seleccion_retoque: true,
      };
    },
  },
  general_manager: {
    label: '👔 Gerente General',
    description: 'Operador completo + ve TODA su sucursal (no solo lo asignado) · mueve leads, atiende, etiquetas · sin otras sucursales ni admin',
    apply: () => {
      const base = operatorFullBase();
      return { ...base, branch_manager: true };
    },
  },
  secretaria: {
    label: '📞 Recepcionista',
    // Rol Recepcionista (Victor 2026-06-07): SOLO Chats. Su función es responder
    // clientes, enviar salesbot y cargar imágenes. Usa plantillas y notas internas.
    // Puede cerrar/resolver conversaciones. NO ve Inicio/Leads/Planes/Informes/
    // Ajustes, NO mueve leads ni cambia etapa, NO borra conversaciones ni clientes,
    // NO asigna tareas. = chat-only.
    description: 'SOLO Chats: responde clientes, envía salesbot, carga imágenes, usa plantillas y notas internas, cierra conversaciones · NO ve Leads/Planes/Informes/Ajustes · NO mueve leads · NO borra conv/clientes · NO asigna tareas',
    apply: () => {
      const modules = {};
      [
        'chat.view',
        'chat.edit',
        'chat.create',
        'salesbot.view',
        'contacts.view',
        'canned_responses.view',
        'internal_notes.view',
        'internal_notes.edit',
        'internal_notes.create',
      ].forEach(k => {
        modules[k] = true;
      });
      // Puede cerrar conversaciones; sin chat.delete, move_*, assign_tasks_to_others,
      // contacts.delete → no borra, no mueve, no asigna tareas.
      return { _mode: 'custom', modules, resolve_conversation: true };
    },
  },
  cuentas_cobrar: {
    label: '🧾 Cuentas por Cobrar',
    // Rol Cuentas por Cobrar (Victor 2026-06-10): factura/cobra. Atiende y escribe,
    // y por cuentas_cobrar_scope ve en su chat (Todos) a TODOS los clientes con
    // baucher detectado o factura cargada aunque no sean suyos; ve el icono 🧾 Por
    // Facturar. En Leads solo entra a Ventas y Clientes (lo limita el backend).
    // MUEVE leads entre etapas y entre embudos, pero como solo ve Ventas y Clientes,
    // el movimiento queda acotado a esos dos departamentos (persona de confianza ·
    // Victor 2026-06-10). Edita la tarjeta del lead/sube factura, edita el nombre del
    // cliente y agrega/quita etiquetas. Cierra conversaciones. NO borra. Menú: Chats +
    // Leads (sin Inicio/Planes/Informes/Ajustes). El scope lo aplica el backend.
    description: 'Atiende y escribe · ve en su chat a TODOS los clientes con baucher/factura detectada aunque no sean suyos y les escribe · ve el icono 🧾 Por Facturar · en Leads solo Ventas y Clientes · MUEVE leads dentro y entre Ventas y Clientes · sube facturas/edita el lead · edita nombre del cliente · agrega/quita etiquetas · cierra conversaciones · NO borra · sin Inicio/Planes/Informes/Ajustes',
    apply: () => {
      const modules = {};
      [
        'chat.view',
        'chat.edit',
        'chat.create',
        'salesbot.view',
        'contacts.view',
        'contacts.edit',
        'canned_responses.view',
        'internal_notes.view',
        'internal_notes.edit',
        'internal_notes.create',
        'leads_deals.view',
        'leads_deals.edit',
        'pipelines.view',
        'stages.view',
        'labels.view',
        'labels.edit',
        'labels.create',
      ].forEach(k => {
        modules[k] = true;
      });
      return {
        _mode: 'custom',
        modules,
        cuentas_cobrar_scope: true,
        move_lead_between_stages: true,
        move_lead_between_pipelines: true,
        resolve_conversation: true,
        hide_planes: true,
      };
    },
  },
  branch_admin: {
    label: '🛡 Admin',
    description: 'Poder TOTAL como el super admin: crea/elimina agentes, da permisos, borra mensajes/contactos, asignación manual, tareas, Ajustes, Informes · cambia entre todas las sucursales · ÚNICO límite: no toca al super admin ni entra a las sucursales personales',
    apply: () => {
      const payload = PRESETS.full_access.apply();
      payload.branch_manager = true;
      return payload;
    },
  },
  fotografo: {
    label: '🎥 Video',
    description:
      'Solo ve leads de Retoque · Álbum · Impresión. Entra al chat y le escribe al cliente, pero NO mueve leads, NO crea ni borra, y NO ve la información de la reserva.',
    // Mantener en sync con AgentPermissions.fotografo_keys.
    apply: () => {
      const modules = {};
      [
        'chat.view',
        'chat.edit',
        'chat.create',
        'leads_deals.view',
        'pipelines.view',
        'stages.view',
        'contacts.view',
      ].forEach(k => {
        modules[k] = true;
      });
      // Sin view_reservations (oculta la reserva) · sin move_* (no mueve) ·
      // fotografo_scope limita la vista a Retoque/Álbum/Impresión.
      return { _mode: 'custom', modules, fotografo_scope: true };
    },
  },
  fotografo_retoque: {
    label: '📷 Fotógrafo',
    description:
      'Solo ve leads de Retoque. Entra a su lead y le escribe al cliente (con saludo de presentación obligatorio una vez al día), pero NO ve el menú Chats ni Planes, NO mueve leads, NO crea/borra, NO ve la reserva ni el botón Configurar.',
    // Mantener en sync con AgentPermissions.fotografo_retoque_keys.
    apply: () => {
      const modules = {};
      [
        'chat.view',
        'chat.edit',
        'chat.create',
        'leads_deals.view',
        'pipelines.view',
        'stages.view',
        'contacts.view',
      ].forEach(k => {
        modules[k] = true;
      });
      // retoque_scope limita la vista SOLO a Retoque + oculta Chats/Planes/Configurar
      // + exige saludo diario.
      return { _mode: 'custom', modules, retoque_scope: true };
    },
  },
  impresion: {
    label: '🖨 Impresión',
    // Rol Impresión · encargada del depto (Victor 2026-06-10). Ve SOLO los embudos
    // Impresión y Álbum de su sucursal favorita — en Leads y en Chats (Todos),
    // incluidos los NO asignados — y les escribe. Menú solo Leads + Chats
    // (hide_planes oculta Planes; sin Inicio/Informes). Mueve leads y gestiona sus
    // columnas, cierra conversaciones. NO es gerente de sucursal (no ve todo).
    description: 'Encargada de Impresión/Álbum: ve SOLO esos 2 embudos en las sucursales que tiene acceso (1 sucursal o varias, según su acceso) — en Leads y en Chats/Todos, aunque no sean sus asignados — y les escribe · mueve leads y gestiona sus columnas · cierra conversaciones · menú solo Leads + Chats · NO ve otros departamentos',
    apply: () => {
      const modules = {};
      [
        'chat.view', 'chat.edit', 'chat.create',
        'internal_notes.view', 'internal_notes.edit', 'internal_notes.create',
        'canned_responses.view',
        'salesbot.view',
        'leads_deals.view', 'leads_deals.edit', 'leads_deals.create',
        'pipelines.view', 'stages.view', 'stages.edit', 'stages.create', 'stages.delete',
        'contacts.view', 'contacts.edit', 'contacts.create',
        'custom_attributes.view', 'custom_attributes.edit',
        'labels.view', 'labels.edit', 'labels.create',
      ].forEach(k => {
        modules[k] = true;
      });
      return {
        _mode: 'custom',
        modules,
        move_lead_between_stages: true,
        move_lead_between_pipelines: true,
        view_reservations: true,
        resolve_conversation: true,
        hide_planes: true,
        impresion_album_scope: true,
      };
    },
  },
  encargada: {
    label: '🏬 Encargada',
    // Rol Encargada (Victor 2026-06-10): Gerente General + ve en Chats (Todos) a
    // TODOS los clientes de TODAS las sucursales y les escribe aunque no sean suyos
    // (view_all_chats). En "Mi Chat" solo sus asignados. En Leads ve SOLO su
    // sucursal (se controla con su acceso a 1 sucursal). Menú Inicio + Leads + Chats.
    description: 'Gerente General + en Chats (Todos) ve los clientes de TODAS las sucursales y les escribe aunque no sean suyos · en Mi Chat solo sus asignados · en Leads ve SOLO su sucursal · mueve leads, edita y cierra',
    apply: () => {
      const base = operatorFullBase();
      return { ...base, branch_manager: true, view_all_chats: true };
    },
  },
};

// Helper: dado feature_permissions guardado, cuenta cuántos "true" hay activos.
export function countActivePermissions(perms) {
  if (!perms || perms._mode !== 'custom') return totalKeysCount(); // legacy = todos
  let count = 0;
  if (perms.modules) {
    Object.values(perms.modules).forEach(v => { if (v === true) count += 1; });
  }
  CATEGORIES.forEach(cat => {
    (cat.specialActions || []).forEach(sa => {
      if (perms[sa.key] === true) count += 1;
    });
  });
  return count;
}

// Helper: aplica/quita TODO en una categoría.
export function applyCategoryAll(perms, categoryKey, value) {
  const cat = CATEGORIES.find(c => c.key === categoryKey);
  if (!cat) return perms;
  const result = { ...perms, _mode: 'custom', modules: { ...(perms.modules || {}) } };
  cat.modules.forEach(mod => {
    mod.actions.forEach(action => { result.modules[`${mod.key}.${action}`] = !!value; });
  });
  (cat.specialActions || []).forEach(sa => { result[sa.key] = !!value; });
  return result;
}

// Helper: aplica/quita TODOS los permisos del catálogo.
export function applyAll(value) {
  if (!value) return { _mode: 'custom', modules: {} };
  return PRESETS.full_access.apply();
}
